import aliases from '~/aliases.json'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

// Nuxt's default useCookie codec JSON-stringifies numeric-looking string values
// (so '1' round-trips as '1' and not the number 1), which stores the raw browser
// cookie as `%221%22` (i.e. `"1"`). The legacy PHP frontend that these cookie names
// and the GTM container triggers were built against always wrote/expects the bare
// value (`setcookie('marketing_cookies', 1, ...)`), so the JSON-quoted format is a
// regression that breaks GTM's consent triggers even though our own dataLayer
// pushes still fire normally. Plain percent-encoding (no JSON) matches the legacy
// wire format. Exported so every other `useCookie` call for these same cookie
// names stays in sync — see also `plugins/repair-consent-cookies.client.ts`, which
// heals cookies already written in the broken quoted format.
export const CONSENT_COOKIE_OPTS = {
  maxAge: COOKIE_MAX_AGE,
  path: '/',
  encode: (v: string) => encodeURIComponent(v),
  decode: (v: string) => decodeURIComponent(v),
} as const
const COOKIE_OPTS = CONSENT_COOKIE_OPTS

const privacyPaths = Object.values(
  (aliases as Record<string, Record<string, string>>).privacy || {}
).flatMap(p => [p, p.replace(/\/$/, '')])

export function useCookieConsent() {
  const route = useRoute()

  const cookieAccepted = useCookie('cookie_accepted', COOKIE_OPTS)
  const functionalCookies = useCookie('functional_cookies', COOKIE_OPTS)
  const statisticCookies = useCookie('statistic_cookies', COOKIE_OPTS)
  const marketingCookies = useCookie('marketing_cookies', COOKIE_OPTS)

  const isPrivacyPage = computed(() => privacyPaths.includes(route.path))

  const shouldShow = computed(() => !cookieAccepted.value && !isPrivacyPage.value)

  function acceptAll() {
    functionalCookies.value = '1'
    statisticCookies.value = '1'
    marketingCookies.value = '1'
    cookieAccepted.value = '1'
    useDataLayer().push({
      event: 'cookie_accepted',
      functional_cookies: '1',
      statistic_cookies: '1',
      marketing_cookies: '1',
    })
    fireImmediatePageview()
  }

  function acceptSelected(options: { statistic: boolean; marketing: boolean }) {
    functionalCookies.value = '1'
    statisticCookies.value = options.statistic ? '1' : '0'
    marketingCookies.value = options.marketing ? '1' : '0'
    cookieAccepted.value = '1'
    useDataLayer().push({
      event: 'cookie_accepted',
      functional_cookies: '1',
      statistic_cookies: options.statistic ? '1' : '0',
      marketing_cookies: options.marketing ? '1' : '0',
    })
    if (options.statistic) fireImmediatePageview()
  }

  function fireImmediatePageview() {
    fireCurrentPageview()
  }

  return {
    shouldShow,
    acceptAll,
    acceptSelected,
  }
}
